import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Badge, Button } from '@/components/atoms';
import { formatarNumero, formatarDataHora } from '@/i18n/formato';
import { destinationsApi } from '@/services/config';
import { motivoLegivel } from '@/services/erroLegivel';
import { detalheLegivel } from '@/services/erros';
import { usePlatformAccess } from '@/services/platform';
import {
  vigiasApi,
  type Rota,
  type SourceKind,
  type TriggerKind,
  type Vigia,
  type VigiaEvent,
} from '@/services/vigias';

import styles from './Vigias.module.css';

/* ═══════════════════════════════════════════════════════════════════════════
   Vigias — A Central. Builder CONDICIONAL: a fonte escolhida revela exatamente
   os campos que aquela função precisa, e o gatilho é restringido ao que a fonte
   suporta. O MESMO formulário cria e edita — os campos não podem divergir.

   Um Vigia é Fonte → Gatilho → Triagem IA → Rota. Uma Editoria é um Vigia cuja
   rota é sempre "auto".
   ═══════════════════════════════════════════════════════════════════════════ */

const SOURCE_LABEL: Record<SourceKind, string> = {
  perfil_social: 'Perfil social',
  feed: 'Feed / RSS',
  periodico: 'Periódico',
  changelog: 'Changelog',
  cluster: 'Cruzamento',
  documento: 'Documento na mão',
};

const TRIGGER_LABEL: Record<TriggerKind, string> = {
  novo_item: 'Item novo na fonte',
  limiar: 'Passou de um limiar',
  ator: 'Ator postou (qualquer coisa)',
  cruzamento: 'Cruzou N fontes',
  spike: 'Acelerando (spike)',
  documento: 'Você entrega o documento',
};
const TRIGGER_SHORT: Record<TriggerKind, string> = {
  novo_item: 'Item novo',
  limiar: 'Limiar de curtidas',
  ator: 'Qualquer post',
  cruzamento: 'Cruzamento',
  spike: 'Acelerando',
  documento: 'Documento entregue',
};

const TRIGGER_META: Record<
  TriggerKind,
  { tag: string; color: string; bg: string; tone: 'crit' | 'info' | 'warn' | 'ok' | 'neutral' }
> = {
  ator: { tag: 'ATOR MONITORADO', color: 'var(--sig-crit)', bg: 'var(--sig-crit-bg)', tone: 'crit' },
  cruzamento: { tag: 'CRUZAMENTO', color: 'var(--sig-info)', bg: 'var(--sig-info-bg)', tone: 'info' },
  limiar: { tag: 'LIMIAR', color: 'var(--sig-warn)', bg: 'var(--sig-warn-bg)', tone: 'warn' },
  novo_item: { tag: 'ITEM NOVO', color: 'var(--sig-ok)', bg: 'var(--sig-ok-bg)', tone: 'ok' },
  spike: { tag: 'SPIKE', color: 'var(--sig-crit)', bg: 'var(--sig-crit-bg)', tone: 'crit' },
  documento: {
    tag: 'FONTE PRIMÁRIA', color: 'var(--sig-info)', bg: 'var(--sig-info-bg)', tone: 'info',
  },
};

const ROUTE_LABEL: Record<Rota, string> = {
  alerta: 'Só me avisar na Central',
  card: 'Criar card no Quadro (semi)',
  auto: 'Produzir sozinho (auto)',
};

const FILTERS: { key: 'tudo' | TriggerKind; label: string }[] = [
  { key: 'tudo', label: 'Tudo' },
  { key: 'documento', label: 'Documentos' },
  { key: 'ator', label: 'Atores' },
  { key: 'cruzamento', label: 'Cruzamentos' },
  { key: 'limiar', label: 'Social' },
  { key: 'novo_item', label: 'Releases' },
];

// Intervalos que o scheduler do backend sabe parsear (scheduler.parse_intervalo).
const INTERVALOS = ['15min', '1h', '4h', '8h', 'diário'];

// Provider de perfil social: só HN e Reddit trazem NÚMEROS (curtidas/comentários).
type SocialProvider = 'hn' | 'reddit' | 'rss';
type FeedMode = 'query' | 'url';

const fmt = { format: (v: number | null | undefined) => formatarNumero(v) };
const isUrl = (s: string) => /^https?:\/\//i.test(s.trim());

// Gatilhos VÁLIDOS por fonte — a coerência que fecha o furo (limiar só onde há
// métrica; cruzamento só no cluster; ator só em perfil).
function allowedTriggers(kind: SourceKind, hasMetrics: boolean): TriggerKind[] {
  if (kind === 'cluster') return ['cruzamento'];
  if (kind === 'documento') return ['documento'];
  if (kind === 'perfil_social') return hasMetrics ? ['ator', 'limiar', 'spike'] : ['ator'];
  return ['novo_item'];
}

/* ─── Decode: vigia salvo → estado do formulário (espelho do buildSource) ─── */
function decProvider(v?: Vigia | null): SocialProvider {
  const s = v?.source;
  if (!s || s.kind !== 'perfil_social') return 'hn';
  if (s.reddit) return 'reddit';
  if (s.url) return 'rss';
  return 'hn';
}
function decAlvo(v?: Vigia | null): string {
  const s = v?.source;
  if (!s) return '';
  if (s.kind === 'perfil_social') return s.reddit ?? s.url ?? '';
  if (s.kind === 'feed') return s.url ?? s.query ?? '';
  return s.url ?? '';
}
function decCluster(v?: Vigia | null): string[] {
  const s = v?.source;
  if (!s || s.kind !== 'cluster') return ['', ''];
  const e = [...(s.urls ?? []), ...(s.queries ?? [])];
  return e.length >= 2 ? e : [...e, '', ''].slice(0, 2);
}

export interface FormPayload {
  nome: string;
  source: Vigia['source'];
  trigger: Vigia['trigger'];
  brief: string;
  rota: Rota;
  pub: Vigia['pub'];
}

/* ─── O formulário — um só, para criar E editar ─────────────────────────── */
function VigiaForm({
  initial,
  siteOpts,
  onSubmit,
  onCancel,
  busy,
}: {
  initial?: Vigia | null;
  siteOpts: { slug: string; name: string }[];
  onSubmit: (p: FormPayload) => void;
  onCancel?: () => void;
  busy: boolean;
}) {
  const editing = !!initial;
  const [nome, setNome] = useState(initial?.nome ?? '');
  const [sourceKind, setSourceKind] = useState<SourceKind>(initial?.source.kind ?? 'perfil_social');
  const [provider, setProvider] = useState<SocialProvider>(() => decProvider(initial));
  const [feedMode, setFeedMode] = useState<FeedMode>(() => (initial?.source.url ? 'url' : 'query'));
  const [alvoText, setAlvoText] = useState(() => decAlvo(initial));
  const [clusterSources, setClusterSources] = useState<string[]>(() => decCluster(initial));
  const [intervalo, setIntervalo] = useState(initial?.source.intervalo ?? '1h');
  // Cadência da fonte primária: 0 = herda o teto de assunto/dia da política.
  const [porDia, setPorDia] = useState(String(initial?.source.cadencia?.por_dia ?? 0));
  const [intervaloMin, setIntervaloMin] = useState(
    String(initial?.source.cadencia?.intervalo_min ?? 90),
  );
  const [triggerKind, setTriggerKind] = useState<TriggerKind>(initial?.trigger.kind ?? 'ator');
  const [limiar, setLimiar] = useState(String(initial?.trigger.limiar ?? 1000));
  const [nFontes, setNFontes] = useState(String(initial?.trigger.n_fontes ?? 2));
  const [brief, setBrief] = useState(initial?.brief ?? '');
  const [rota, setRota] = useState<Rota>(initial?.rota ?? 'alerta');
  const [destSites, setDestSites] = useState<string[]>(initial?.pub?.sites ?? []);
  const [categoria, setCategoria] = useState(initial?.pub?.categoria ?? '');
  const [status, setStatus] = useState<'draft' | 'publish'>(initial?.pub?.status ?? 'draft');

  const hasMetrics = sourceKind === 'perfil_social' && (provider === 'hn' || provider === 'reddit');
  const triggers = allowedTriggers(sourceKind, hasMetrics);

  // Mantém o gatilho SEMPRE coerente com a fonte (fecha o furo do limiar num feed).
  useEffect(() => {
    if (!triggers.includes(triggerKind)) setTriggerKind(triggers[0]);
  }, [triggers, triggerKind]);

  const changeSource = (k: SourceKind) => {
    setSourceKind(k);
    setAlvoText('');
    if (k === 'perfil_social') setProvider('hn');
    if (k === 'feed') setFeedMode('query');
    if (k === 'cluster') setClusterSources(['', '']);
  };

  const setCluster = (i: number, v: string) =>
    setClusterSources((prev) => prev.map((x, j) => (j === i ? v : x)));
  const addCluster = () => setClusterSources((prev) => [...prev, '']);
  const delCluster = (i: number) =>
    setClusterSources((prev) => (prev.length <= 2 ? prev : prev.filter((_, j) => j !== i)));

  const sourceReady = useMemo(() => {
    if (sourceKind === 'perfil_social') return provider === 'hn' || alvoText.trim().length > 0;
    if (sourceKind === 'cluster')
      return clusterSources.filter((x) => x.trim()).length >= (Number(nFontes) || 2);
    // Documento não tem endereço para configurar: a fonte é o material que
    // você vai entregar. Basta o nome do vigia.
    if (sourceKind === 'documento') return true;
    return alvoText.trim().length > 0;
  }, [sourceKind, provider, alvoText, clusterSources, nFontes]);

  const destReady = rota === 'alerta' || destSites.length > 0;
  const canSubmit = nome.trim().length > 0 && sourceReady && destReady;

  const buildSource = (): Vigia['source'] => {
    // Documento não tem intervalo: não há o que consultar de tempos em tempos.
    // O que se agenda aqui é a SAÍDA (cadência das matérias), não a busca.
    if (sourceKind === 'documento') {
      return {
        kind: 'documento',
        alvo: nome.trim(),
        cadencia: {
          por_dia: Math.max(0, Number(porDia) || 0),
          intervalo_min: Math.max(0, Number(intervaloMin) || 0),
        },
      };
    }
    const s: Vigia['source'] = { kind: sourceKind, alvo: nome.trim(), intervalo };
    if (sourceKind === 'perfil_social') {
      if (provider === 'hn') s.hn = true;
      else if (provider === 'reddit') s.reddit = alvoText.trim().replace(/^r\//i, '');
      else s.url = alvoText.trim();
    } else if (sourceKind === 'feed') {
      if (feedMode === 'url') s.url = alvoText.trim();
      else s.query = alvoText.trim();
    } else if (sourceKind === 'periodico' || sourceKind === 'changelog') {
      s.url = alvoText.trim();
    } else if (sourceKind === 'cluster') {
      const entries = clusterSources.map((x) => x.trim()).filter(Boolean);
      s.urls = entries.filter(isUrl);
      s.queries = entries.filter((x) => !isUrl(x));
    }
    return s;
  };

  const submit = () => {
    const trigger: Vigia['trigger'] = { kind: triggerKind };
    if (triggerKind === 'limiar' || triggerKind === 'spike') trigger.limiar = Number(limiar) || 0;
    if (triggerKind === 'cruzamento') trigger.n_fontes = Number(nFontes) || 2;
    onSubmit({
      nome: nome.trim(),
      source: buildSource(),
      trigger,
      brief: brief.trim(),
      rota,
      pub: { sites: destSites, categoria: categoria.trim() || undefined, status },
    });
  };

  return (
    <div className={styles.card}>
      <h2 className={styles.cardTitle}>
        {editing ? <IconEdit /> : <IconPlus />} {editing ? `Editando: ${initial?.nome}` : 'Novo vigia'}
      </h2>
      <p className={styles.cardSub}>Vigiar → Analisar → Rotear</p>

      <input
        className={styles.input}
        value={nome}
        placeholder="Nome do vigia (ex.: Donald Trump, Releases de IA)"
        onChange={(ev) => setNome(ev.target.value)}
      />

      {/* 1 · FONTE */}
      <div className={styles.step}>1 · Fonte</div>
      <div className={styles.sourceGrid}>
        {(Object.keys(SOURCE_LABEL) as SourceKind[]).map((k) => (
          <button
            key={k}
            type="button"
            className={`${styles.sourceOpt} ${sourceKind === k ? styles.sourceOptOn : ''}`}
            onClick={() => changeSource(k)}
          >
            <SourceIcon kind={k} />
            {SOURCE_LABEL[k]}
          </button>
        ))}
      </div>

      {sourceKind === 'perfil_social' && (
        <>
          <div className={styles.subChips}>
            {([['hn', 'Hacker News'], ['reddit', 'Reddit'], ['rss', 'RSS do perfil']] as [SocialProvider, string][]).map(
              ([p, lbl]) => (
                <button
                  key={p}
                  type="button"
                  className={`${styles.subChip} ${provider === p ? styles.subChipOn : ''}`}
                  onClick={() => setProvider(p)}
                >
                  {lbl}
                </button>
              ),
            )}
          </div>
          {provider === 'hn' && (
            <div className={styles.lockNote}><IconInfo /> Front page do Hacker News (com números).</div>
          )}
          {provider === 'reddit' && (
            <input className={styles.input} value={alvoText} placeholder="subreddit (ex.: technology)" onChange={(e) => setAlvoText(e.target.value)} />
          )}
          {provider === 'rss' && (
            <input className={styles.input} value={alvoText} placeholder="URL do RSS do perfil (ex.: Bluesky /rss)" onChange={(e) => setAlvoText(e.target.value)} />
          )}
        </>
      )}

      {sourceKind === 'feed' && (
        <>
          <div className={styles.subChips}>
            {([['query', 'Termo (Google News)'], ['url', 'URL de RSS']] as [FeedMode, string][]).map(([m, lbl]) => (
              <button
                key={m}
                type="button"
                className={`${styles.subChip} ${feedMode === m ? styles.subChipOn : ''}`}
                onClick={() => setFeedMode(m)}
              >
                {lbl}
              </button>
            ))}
          </div>
          <input
            className={styles.input}
            value={alvoText}
            placeholder={feedMode === 'query' ? 'termo de busca (ex.: eleições, São Paulo)' : 'https://veiculo.com.br/feed'}
            onChange={(e) => setAlvoText(e.target.value)}
          />
        </>
      )}

      {sourceKind === 'periodico' && (
        <input className={styles.input} value={alvoText} placeholder="URL do RSS do periódico (ex.: NEJM, Lancet)" onChange={(e) => setAlvoText(e.target.value)} />
      )}
      {sourceKind === 'changelog' && (
        <input className={styles.input} value={alvoText} placeholder="URL do changelog em RSS (ex.: github.blog/changelog/feed)" onChange={(e) => setAlvoText(e.target.value)} />
      )}

      {sourceKind === 'cluster' && (
        <>
          <span className={styles.help}>As fontes a cruzar (termo do Google News ou URL de RSS). Precisa de pelo menos {Number(nFontes) || 2}.</span>
          <div className={styles.srcList}>
            {clusterSources.map((s, i) => (
              <div key={i} className={styles.srcRow}>
                <input className={styles.input} value={s} placeholder={`fonte ${i + 1} — termo ou URL`} onChange={(e) => setCluster(i, e.target.value)} />
                <button type="button" className={styles.srcDel} title="remover" onClick={() => delCluster(i)} disabled={clusterSources.length <= 2}>×</button>
              </div>
            ))}
          </div>
          <button type="button" className={styles.srcAdd} onClick={addCluster}>+ fonte</button>
        </>
      )}

      {sourceKind === 'documento' && (
        <>
          <div className={styles.lockNote}>
            <IconInfo /> Esta fonte não vasculha a internet: você entrega o material
            (PDF, vídeo ou texto) e ela lê. Antes de escrever qualquer coisa, decide
            se aquilo é notícia — e recusa se não for.
          </div>
          <span className={styles.help}>
            Cadência: um documento com muitas matérias dentro não vai todo ao ar de
            uma vez. A primeira sai na hora; as demais entram na fila.
          </span>
          <div className={styles.inline}>
            <span className={styles.inlineLabel}>No máximo</span>
            <input
              className={styles.input}
              value={porDia}
              onChange={(e) => setPorDia(e.target.value)}
              style={{ maxWidth: 64 }}
            />
            <span className={styles.inlineLabel}>por dia (0 = usar o teto da casa), a cada</span>
            <input
              className={styles.input}
              value={intervaloMin}
              onChange={(e) => setIntervaloMin(e.target.value)}
              style={{ maxWidth: 72 }}
            />
            <span className={styles.inlineLabel}>min</span>
          </div>
        </>
      )}

      {/* Frequência — o worker precisa saber de quanto em quanto tempo buscar. */}
      {sourceKind !== 'documento' && (
        <div className={styles.inline}>
          <span className={styles.inlineLabel}>Verificar a cada</span>
          <select className={styles.input} value={intervalo} onChange={(e) => setIntervalo(e.target.value)}>
            {INTERVALOS.map((i) => (
              <option key={i} value={i}>{i}</option>
            ))}
          </select>
        </div>
      )}

      {/* 2 · GATILHO — restringido pela fonte */}
      <div className={styles.step}>2 · Gatilho</div>
      {triggers.length > 1 ? (
        <div className={styles.subChips}>
          {triggers.map((t) => (
            <button
              key={t}
              type="button"
              className={`${styles.subChip} ${triggerKind === t ? styles.subChipOn : ''}`}
              onClick={() => setTriggerKind(t)}
            >
              {TRIGGER_SHORT[t]}
            </button>
          ))}
        </div>
      ) : (
        <div className={styles.lockNote}>
          <IconInfo /> {TRIGGER_LABEL[triggers[0]]}
          {triggers[0] === 'novo_item' ? ' — dispara a cada item novo.' : ''}
        </div>
      )}
      {(triggerKind === 'limiar' || triggerKind === 'spike') && (
        <div className={styles.inline}>
          <span className={styles.inlineLabel}>Curtidas ≥</span>
          <input className={styles.input} value={limiar} onChange={(e) => setLimiar(e.target.value)} />
        </div>
      )}
      {triggerKind === 'cruzamento' && (
        <div className={styles.inline}>
          <span className={styles.inlineLabel}>Mesmo assunto em ≥</span>
          <input className={styles.input} value={nFontes} onChange={(e) => setNFontes(e.target.value)} style={{ maxWidth: 64 }} />
          <span className={styles.inlineLabel}>fontes</span>
        </div>
      )}

      <input
        className={`${styles.input} ${styles.brief}`}
        value={brief}
        placeholder="Brief da IA: o que torna isto relevante para o seu público"
        onChange={(e) => setBrief(e.target.value)}
      />
      <span className={styles.help}>
        A IA lê cada disparo contra o brief e decide a rota. É o que separa vigilância de spam.
      </span>

      {/* 3 · ROTA + destino */}
      <div className={styles.step}>3 · Rota</div>
      <div className={styles.routes}>
        {(Object.keys(ROUTE_LABEL) as Rota[]).map((rt) => (
          <button
            key={rt}
            type="button"
            className={`${styles.routeOpt} ${rota === rt ? styles.routeOptOn : ''}`}
            onClick={() => setRota(rt)}
          >
            <RouteIcon kind={rt} />
            {ROUTE_LABEL[rt]}
            <span className={styles.routeHint}>
              {rt === 'alerta' ? 'te avisa' : rt === 'card' ? 'semi' : 'sem você'}
            </span>
          </button>
        ))}
      </div>

      <div className={styles.step}>
        Destino {rota === 'alerta' ? '(opcional — usado se você clicar “Produzir”)' : '(obrigatório p/ esta rota)'}
      </div>
      <div className={styles.destSites}>
        {siteOpts.length === 0 && <span className={styles.help}>Nenhum site cadastrado em Config → Sites.</span>}
        {siteOpts.map((s) => (
          <label key={s.slug} className={styles.destChk}>
            <input
              type="checkbox"
              checked={destSites.includes(s.slug)}
              onChange={(e) =>
                setDestSites((prev) => (e.target.checked ? [...prev, s.slug] : prev.filter((x) => x !== s.slug)))
              }
            />
            {s.name}
          </label>
        ))}
      </div>
      {rota !== 'alerta' && destSites.length === 0 && (
        <span className={styles.reqNote}>Escolha ao menos um site — {rota === 'auto' ? 'a rota auto publica sozinha' : 'o card vira post ao produzir'}.</span>
      )}
      <div className={styles.inline}>
        <input className={styles.input} value={categoria} placeholder="Categoria no WP (ex.: Tecnologia)" onChange={(e) => setCategoria(e.target.value)} />
        <select className={styles.input} value={status} onChange={(e) => setStatus(e.target.value as 'draft' | 'publish')} style={{ maxWidth: 130 }}>
          <option value="draft">Rascunho</option>
          <option value="publish">Publica</option>
        </select>
      </div>

      <div className={styles.formBtns}>
        {editing && (
          <Button variant="secondary" onClick={onCancel} style={{ flex: 1 }}>Cancelar</Button>
        )}
        <Button onClick={submit} disabled={!canSubmit} loading={busy} style={{ flex: 2 }}>
          {editing ? 'Salvar alterações' : 'Ligar vigia'}
        </Button>
      </div>
    </div>
  );
}

export function Vigias() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<'tudo' | TriggerKind>('tudo');
  const [tickMsg, setTickMsg] = useState<string | null>(null);
  const [produceErr, setProduceErr] = useState<{ id: string; msg: string } | null>(null);
  const [editing, setEditing] = useState<Vigia | null>(null);

  // POST /vigias/tick-all roda as vigias de TODOS os tenants e por isso é
  // staff-only no backend. Enquanto a sonda de staff não responde não se mostra
  // nem o botão nem o aviso do tenant — senão o staff lê, por um instante, um
  // texto que não vale para ele.
  const { isStaff, query: sondaStaff } = usePlatformAccess();

  const vigias = useQuery({ queryKey: ['vigias'], queryFn: vigiasApi.list });
  const eventos = useQuery({ queryKey: ['vigia-eventos'], queryFn: () => vigiasApi.eventos() });
  const resumo = useQuery({ queryKey: ['vigia-resumo'], queryFn: vigiasApi.resumo });
  const sites = useQuery({ queryKey: ['wp-sites'], queryFn: () => destinationsApi.list() });

  const refetchAll = () => {
    qc.invalidateQueries({ queryKey: ['vigia-eventos'] });
    qc.invalidateQueries({ queryKey: ['vigia-resumo'] });
    qc.invalidateQueries({ queryKey: ['vigias'] });
  };

  const tickAll = useMutation({
    mutationFn: vigiasApi.tickAll,
    onSuccess: (r) => {
      setTickMsg(`${r.total} novo(s) evento(s) em ${r.vigias.length} vigias.`);
      refetchAll();
    },
    // A mensagem antiga ("veja os logs do backend") errava duas vezes: chamava
    // de falha o que muitas vezes era recusa, e mandava o operador a um log que
    // ele não tem. O que se mostra é o literal que o backend escreveu.
    onError: (e: unknown) => setTickMsg(detalheLegivel(e, motivoLegivel(e))),
  });

  const produzir = useMutation({
    mutationFn: (id: string) => vigiasApi.produzir(id),
    onSuccess: () => {
      setProduceErr(null);
      refetchAll();
    },
    // Sem destino o backend responde 422 com {erro, motivo, como_resolver} —
    // a terceira parte é a que diz ONDE resolver (Config → Sites WordPress), e
    // era a que o leitor antigo desta tela descartava. `motivoLegivel` fica de
    // fallback para quando o backend não manda `detail` nenhum.
    onError: (e: unknown, id) =>
      setProduceErr({ id, msg: detalheLegivel(e, motivoLegivel(e)) }),
  });
  const dispensar = useMutation({
    mutationFn: (id: string) => vigiasApi.dispensar(id),
    onSuccess: refetchAll,
  });
  const toggle = useMutation({
    mutationFn: (v: { slug: string; ativo: boolean }) => vigiasApi.patch(v.slug, { ativo: v.ativo }),
    onSuccess: refetchAll,
  });

  const salvar = useMutation({
    mutationFn: (p: FormPayload) => {
      if (editing) return vigiasApi.patch(editing.slug, p);
      const slug = `${p.nome.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40) || 'vigia'}-${Date.now().toString(36).slice(-4)}`;
      return vigiasApi.create({ slug, ...p });
    },
    onSuccess: () => {
      setEditing(null);
      refetchAll();
    },
  });

  const visiveis = useMemo(
    () => (eventos.data ?? []).filter((e) => filter === 'tudo' || e.trigger_kind === filter),
    [eventos.data, filter],
  );
  const countFor = (key: 'tudo' | TriggerKind) =>
    (eventos.data ?? []).filter((e) => key === 'tudo' || e.trigger_kind === key).length;

  const r = resumo.data;
  const siteOpts = (sites.data ?? []).map((s) => ({ slug: s.slug, name: s.name || s.slug }));

  return (
    <section className={styles.root}>
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>Vigias</h1>
          <p className={styles.subtitle}>
            Vigilância personalizada. Cadastre fontes, defina o gatilho, e o sistema bate no seu ombro
            quando algo acontece — ou produz sozinho quando é óbvio. Um vigia é{' '}
            <strong>Fonte → Gatilho → Triagem da IA → Rota</strong>; uma editoria é um vigia cuja rota é sempre “produzir”.
          </p>
        </div>
        {isStaff ? (
          <Button onClick={() => tickAll.mutate()} loading={tickAll.isPending}>
            Rodar vigias agora
          </Button>
        ) : (
          !sondaStaff.isLoading && (
            <span className={styles.lockNote} style={{ maxWidth: 320 }}>
              <IconInfo /> As vigias rodam sozinhas, cada uma no intervalo que você
              escolheu — rodar manualmente é operação da casa.
            </span>
          )
        )}
      </header>

      <div className={styles.resumo}>
        <span className={styles.chip}><b>{r?.total ?? '—'}</b> vigias</span>
        <span className={`${styles.chip} ${styles.chipOk}`}><b>{r?.ativos ?? '—'}</b> ativos</span>
        <span className={`${styles.chip} ${styles.chipInfo}`}><b>{r?.pendentes ?? '—'}</b> pendentes</span>
        <span className={`${styles.chip} ${styles.chipWarn}`}><b>{r?.produzidos ?? '—'}</b> produzidos</span>
        {tickMsg && <span className={styles.proto}>{tickMsg}</span>}
      </div>

      <div className={styles.filters} role="tablist" aria-label="Filtrar o stream">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            role="tab"
            aria-selected={filter === f.key}
            className={`${styles.filterBtn} ${filter === f.key ? styles.filterBtnOn : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
            <span className={styles.filterCount}>{countFor(f.key)}</span>
          </button>
        ))}
      </div>

      <div className={styles.cols}>
        {/* ─── STREAM ────────────────────────────────────────────────────── */}
        <div className={styles.stream}>
          <div className={styles.streamHead}>
            <span>Descoberta · o que precisa de você agora</span>
            <span>{visiveis.length} eventos</span>
          </div>

          {eventos.isLoading && <div className={styles.streamEmpty}>Carregando…</div>}

          {eventos.isSuccess && visiveis.length === 0 && (
            <div className={styles.streamEmpty}>
              Nenhum evento ainda. Os eventos aparecem aqui quando as vigias rodarem — cada
              uma no intervalo que você escolheu (a partir de 15 min).
              {isStaff && (
                <>
                  {' '}Para adiantar, use <strong>“Rodar vigias agora”</strong>.
                </>
              )}
            </div>
          )}

          {visiveis.map((e) => (
            <EventCard
              key={e.id}
              e={e}
              onProduzir={() => produzir.mutate(e.id)}
              onDispensar={() => dispensar.mutate(e.id)}
              busy={
                (produzir.isPending && produzir.variables === e.id) ||
                (dispensar.isPending && dispensar.variables === e.id)
              }
              err={produceErr?.id === e.id ? produceErr.msg : null}
            />
          ))}
        </div>

        {/* ─── PAINEL: mesa de documentos + form (criar/editar) + lista ──── */}
        <aside className={styles.panel}>
          <MesaDeDocumentos vigias={vigias.data ?? []} onAnalisado={refetchAll} />

          <VigiaForm
            key={editing?.slug ?? 'novo'}
            initial={editing}
            siteOpts={siteOpts}
            onSubmit={(p) => salvar.mutate(p)}
            onCancel={() => setEditing(null)}
            busy={salvar.isPending}
          />

          <div className={styles.card}>
            <h2 className={styles.cardTitle}><IconRadar /> Vigias ({vigias.data?.length ?? 0})</h2>
            <p className={styles.cardSub}>O que você está monitorando.</p>
            <ul className={styles.vigList}>
              {(vigias.data ?? []).map((v) => (
                <li key={v.slug} className={`${styles.vigItem} ${editing?.slug === v.slug ? styles.vigItemOn : ''}`}>
                  <span className={`${styles.vigDot} ${v.ativo ? styles.vigDotOn : ''}`} />
                  <div className={styles.vigBody}>
                    <span className={styles.vigName}>{v.nome}</span>
                    <span className={styles.vigMeta}>
                      {SOURCE_LABEL[v.source.kind]} · {TRIGGER_SHORT[v.trigger.kind]}
                      {v.source.intervalo ? ` · ${v.source.intervalo}` : ''}
                      {v.pub?.sites?.length ? ` · → ${v.pub.sites.join(', ')}` : ''}
                    </span>
                  </div>
                  <Badge tone={TRIGGER_META[v.trigger.kind].tone} size="xs">
                    {v.rota === 'auto' ? 'auto' : v.rota === 'card' ? 'semi' : 'alerta'}
                  </Badge>
                  <button className={styles.vigToggle} onClick={() => setEditing(v)} title="editar">
                    editar
                  </button>
                  <button
                    className={styles.vigToggle}
                    onClick={() => toggle.mutate({ slug: v.slug, ativo: !v.ativo })}
                  >
                    {v.ativo ? 'pausar' : 'ligar'}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MESA DE DOCUMENTOS — a tela de uso da fonte primária.

   O material entra (PDF, vídeo ou texto), e a leitura acontece à vista: cada
   etapa aparece na barra. Isso não é enfeite. A análise leva minutos, e uma
   tela parada durante minutos é indistinguível de uma tela quebrada — o
   operador reenvia o arquivo, e aí sim há duas leituras cobradas.

   O resultado tem DOIS desfechos possíveis, e o recusado é tão importante
   quanto o aprovado: quando a trava diz "isto não é notícia", ela mostra o
   motivo. É assim que o operador aprende o que a máquina considera pauta.
   ═══════════════════════════════════════════════════════════════════════════ */
const ESTADO_TERMINAL = ['analisado', 'recusado', 'concluido', 'falhou'];

function MesaDeDocumentos({
  vigias,
  onAnalisado,
}: {
  vigias: Vigia[];
  onAnalisado: () => void;
}) {
  const docVigias = useMemo(
    () => vigias.filter((v) => v.source.kind === 'documento'),
    [vigias],
  );
  const [slug, setSlug] = useState('');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [texto, setTexto] = useState('');
  const [origem, setOrigem] = useState('');
  const [docId, setDocId] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!slug && docVigias.length) setSlug(docVigias[0].slug);
  }, [docVigias, slug]);

  // A barra de evolução: pergunta o estado enquanto o trabalho corre e para
  // sozinha quando ele acaba (`refetchInterval` false = deixa de perguntar).
  const doc = useQuery({
    queryKey: ['vigia-doc', docId],
    queryFn: () => vigiasApi.documento(docId as string),
    enabled: !!docId,
    refetchInterval: (q) =>
      q.state.data && ESTADO_TERMINAL.includes(q.state.data.estado) ? false : 2000,
  });

  useEffect(() => {
    if (doc.data && ESTADO_TERMINAL.includes(doc.data.estado)) onAnalisado();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.data?.estado]);

  const enviar = useMutation({
    mutationFn: () =>
      vigiasApi.enviarDocumento(slug, {
        arquivo: arquivo ?? undefined,
        texto: texto.trim() || undefined,
        nome: arquivo?.name,
        origem: origem.trim() || undefined,
      }),
    onSuccess: (d) => {
      setErro(null);
      setDocId(d.id);
      setArquivo(null);
      setTexto('');
    },
    onError: (e: unknown) => setErro(e instanceof Error ? e.message : 'falha ao enviar'),
  });

  const d = doc.data;
  const pct = d?.progresso?.pct ?? 0;
  const emCurso = !!d && !ESTADO_TERMINAL.includes(d.estado);
  const pronto = !!arquivo || texto.trim().length > 0;

  if (docVigias.length === 0) {
    return (
      <div className={styles.card}>
        <h2 className={styles.cardTitle}><IconDoc /> Mesa de documentos</h2>
        <p className={styles.cardSub}>
          Nenhuma vigia de fonte primária ainda. Crie uma com a fonte{' '}
          <strong>“Documento na mão”</strong> e ela passa a receber PDF, vídeo e texto —
          decidindo sozinha se aquilo vira notícia.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.card}>
      <h2 className={styles.cardTitle}><IconDoc /> Mesa de documentos</h2>
      <p className={styles.cardSub}>
        Entregue o material. A vigia lê, decide se é notícia, diz quantas matérias
        existem ali e com que cadência elas vão ao ar.
      </p>

      <div className={styles.inline}>
        <span className={styles.inlineLabel}>Vigia</span>
        <select className={styles.input} value={slug} onChange={(e) => setSlug(e.target.value)}>
          {docVigias.map((v) => (
            <option key={v.slug} value={v.slug}>{v.nome}</option>
          ))}
        </select>
      </div>

      <label className={styles.dropZone}>
        <input
          type="file"
          accept=".pdf,.txt,.md,.mp4,.mp3,.m4a,.wav,.webm,.mov"
          onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
        />
        <span>
          {arquivo
            ? `${arquivo.name} · ${(arquivo.size / 1024 / 1024).toFixed(1)} MB`
            : 'Escolher arquivo — PDF, vídeo ou áudio (até 25 MB)'}
        </span>
      </label>

      <textarea
        className={`${styles.input} ${styles.docTexto}`}
        value={texto}
        placeholder="…ou cole aqui o teor do documento"
        onChange={(e) => setTexto(e.target.value)}
      />
      <input
        className={styles.input}
        value={origem}
        placeholder="Origem, como sairá na matéria (ex.: STF · decisão da PET 16.435)"
        onChange={(e) => setOrigem(e.target.value)}
      />

      <Button
        onClick={() => enviar.mutate()}
        disabled={!pronto || !slug}
        loading={enviar.isPending || emCurso}
        style={{ width: '100%' }}
      >
        {emCurso ? 'Lendo o documento…' : 'Ler documento'}
      </Button>
      {erro && <span className={styles.reqNote}>{erro}</span>}

      {/* ─── Barra de evolução ─────────────────────────────────────────── */}
      {d && (
        <div className={styles.progWrap}>
          <div className={styles.progBar}>
            <div
              className={`${styles.progFill} ${d.estado === 'recusado' ? styles.progFillNo : ''} ${
                d.estado === 'falhou' ? styles.progFillErr : ''
              }`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className={styles.progText}>
            {d.progresso?.mensagem ?? d.estado} · {pct}%
            {d.tipo === 'video' && d.meta?.duracao_seg
              ? ` · ${Math.round(d.meta.duracao_seg / 60)} min de vídeo`
              : ''}
            {d.paginas ? ` · ${d.paginas} páginas` : ''}
          </span>
        </div>
      )}

      {/* ─── A TRAVA falou não ──────────────────────────────────────────── */}
      {d?.estado === 'recusado' && (
        <div className={styles.recusa}>
          <strong>Não virou pauta.</strong> {d.plano?.motivo}
          <span className={styles.help}>
            Nenhuma matéria foi escrita e nada foi cobrado de redação — a trava roda
            antes da produção.
          </span>
        </div>
      )}

      {d?.estado === 'falhou' && (
        <div className={styles.recusa}>
          <strong>Falhou.</strong> {d.progresso?.mensagem}
        </div>
      )}

      {/* ─── O plano de pauta ───────────────────────────────────────────── */}
      {d?.plano?.pautas && d.plano.pautas.length > 0 && (
        <div className={styles.plano}>
          <div className={styles.planoHead}>
            <strong>{d.plano.pautas.length} matérias</strong> neste documento
            {d.plano.cadencia && (
              <span>
                {' '}· até {d.plano.cadencia.por_dia}/dia, a cada{' '}
                {d.plano.cadencia.intervalo_min} min ({d.plano.cadencia.dias} dia(s))
              </span>
            )}
          </div>
          {d.plano.resumo && <p className={styles.planoResumo}>{d.plano.resumo}</p>}

          {d.plano.cuidados && d.plano.cuidados.length > 0 && (
            <details className={styles.cuidados}>
              <summary>{d.plano.cuidados.length} cuidados obrigatórios nesta pauta</summary>
              <ul>
                {d.plano.cuidados.map((c, i) => <li key={i}>{c}</li>)}
              </ul>
            </details>
          )}

          {d.plano.pautas.map((p, i) => (
            <article key={i} className={styles.pautaCard}>
              <header>
                <Badge tone={p.forca === 'alta' ? 'crit' : p.forca === 'media' ? 'warn' : 'neutral'} size="xs">
                  {p.forca}
                </Badge>
                <span className={styles.pautaNum}>#{i + 1}</span>
                <h4>{p.titulo}</h4>
              </header>
              {p.angulo && <p className={styles.pautaAngulo}>{p.angulo}</p>}
              {p.sustentacao && (
                <blockquote className={styles.pautaSust}>{p.sustentacao}</blockquote>
              )}
              {p.fatos?.length > 0 && (
                <ul className={styles.pautaFatos}>
                  {p.fatos.map((f, j) => <li key={j}>{f}</li>)}
                </ul>
              )}
              {p.falta_apurar?.length > 0 && (
                <div className={styles.pautaFalta}>
                  <strong>Falta apurar:</strong> {p.falta_apurar.join(' · ')}
                </div>
              )}
            </article>
          ))}
          <span className={styles.help}>
            Os cards já estão no stream ao lado, com a hora marcada de cada um.
          </span>
        </div>
      )}
    </div>
  );
}

function EventCard({
  e,
  onProduzir,
  onDispensar,
  busy,
  err,
}: {
  e: VigiaEvent;
  onProduzir: () => void;
  onDispensar: () => void;
  busy: boolean;
  err: string | null;
}) {
  const meta = TRIGGER_META[e.trigger_kind];
  const done = e.estado !== 'pendente';
  const rel = e.triage?.relevancia || '—';
  const iaTexto = e.triage?.angulo || e.triage?.motivo || '';
  return (
    <article
      className={`${styles.event} ${done ? styles.eventDone : ''}`}
      style={{ ['--evtColor' as string]: meta.color }}
    >
      <div className={styles.evtTop}>
        <span className={styles.trigTag} style={{ color: meta.color, background: meta.bg }}>
          {meta.tag}
          {e.trigger_kind === 'cruzamento' && e.fontes.length ? ` · ${e.fontes.length} FONTES` : ''}
        </span>
        <span className={styles.evtMeta}>
          {e.vigia_nome}{e.origem ? ` · ${e.origem}` : ''}
        </span>
      </div>

      <p className={styles.evtTitle}>{e.titulo}</p>

      {/* Hora marcada pela cadência — o operador precisa saber que a matéria
          não sumiu: ela tem hora para sair. Sem isto, uma pauta agendada
          parece uma pauta esquecida. */}
      {e.estado === 'pendente' && e.publicar_em && (
        <span className={styles.agendado}>
          <IconClock /> sai {formatarDataHora(e.publicar_em, {
            day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
          })}
        </span>
      )}

      {e.triage?.falta_apurar && e.triage.falta_apurar.length > 0 && (
        <div className={styles.evtFalta}>
          <strong>Falta apurar:</strong> {e.triage.falta_apurar.join(' · ')}
        </div>
      )}

      {(e.metrics?.curtidas != null || e.metrics?.comentarios != null) && (
        <div className={styles.metrics}>
          {e.metrics.curtidas != null && (
            <span className={styles.metric}>
              <IconHeart /> {fmt.format(e.metrics.curtidas)}
              {e.metrics.limiar != null && (
                <span className={styles.metricThreshold}> / {fmt.format(e.metrics.limiar)}</span>
              )}
            </span>
          )}
          {e.metrics.reposts != null && (
            <span className={styles.metric}><IconRepeat /> {fmt.format(e.metrics.reposts)}</span>
          )}
          {e.metrics.comentarios != null && (
            <span className={styles.metric}><IconChat /> {fmt.format(e.metrics.comentarios)}</span>
          )}
          {e.metrics.spike && (
            <span className={`${styles.metric} ${styles.metricHot}`}><IconFlame /> acelerando</span>
          )}
        </div>
      )}

      {e.fontes.length > 0 && (
        <div className={styles.sources}>
          {e.fontes.map((f) => (
            <span key={f} className={styles.sourceChip}>{f}</span>
          ))}
        </div>
      )}

      {iaTexto && (
        <div className={styles.ia}>
          <IconSpark />
          <span>
            <b>IA:</b> <span className={styles.iaRel}>relevância {rel}</span> — {iaTexto}
          </span>
        </div>
      )}

      <div className={styles.evtActions}>
        {e.estado === 'produzido' ? (
          <span className={styles.doneTag}>
            <IconCheck /> Produzido
            {e.wp_link && (
              <>
                {' · '}
                <a href={e.wp_link} target="_blank" rel="noreferrer" style={{ color: 'var(--nexus-brand)' }}>
                  ver no WordPress →
                </a>
              </>
            )}
          </span>
        ) : e.estado === 'rascunho' ? (
          <span className={styles.doneTag}><IconCheck /> Card no Quadro</span>
        ) : (
          <>
            <Button size="sm" onClick={onProduzir} loading={busy}>Produzir</Button>
            <button className={styles.dismissBtn} onClick={onDispensar} disabled={busy}>
              Dispensar
            </button>
          </>
        )}
      </div>
      {err && <p className={styles.pubErr}>{err}</p>}
    </article>
  );
}

/* ─── Ícones inline ──────────────────────────────────────────────────────── */
function Svg({ children }: { children: ReactNode }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {children}
    </svg>
  );
}
const IconHeart = () => <Svg><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z" /></Svg>;
const IconRepeat = () => <Svg><path d="m17 2 4 4-4 4" /><path d="M3 11v-1a4 4 0 0 1 4-4h14" /><path d="m7 22-4-4 4-4" /><path d="M21 13v1a4 4 0 0 1-4 4H3" /></Svg>;
const IconChat = () => <Svg><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></Svg>;
const IconFlame = () => <Svg><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" /></Svg>;
const IconSpark = () => <Svg><path d="M12 3v4M12 17v4M3 12h4M17 12h4M6.3 6.3l2.8 2.8M14.9 14.9l2.8 2.8M17.7 6.3l-2.8 2.8M9.1 14.9l-2.8 2.8" /></Svg>;
const IconCheck = () => <Svg><path d="M20 6 9 17l-5-5" /></Svg>;
const IconPlus = () => <Svg><path d="M12 5v14M5 12h14" /></Svg>;
const IconEdit = () => <Svg><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></Svg>;
const IconInfo = () => <Svg><circle cx="12" cy="12" r="9" /><path d="M12 16v-4M12 8h.01" /></Svg>;
const IconRadar = () => <Svg><path d="M19.07 4.93A10 10 0 0 0 6.99 3.34" /><path d="M4 6h.01" /><path d="M2.29 9.62A10 10 0 1 0 21.31 8.35" /><path d="M16.24 7.76A6 6 0 1 0 8.23 16.67" /><path d="M12 18h.01" /><path d="M17.99 11.66A6 6 0 0 1 15.77 16.67" /><circle cx="12" cy="12" r="2" /><path d="m13.41 10.59 5.66-5.66" /></Svg>;
const IconBell = () => <Svg><path d="M10.268 21a2 2 0 0 0 3.464 0" /><path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326" /></Svg>;
const IconBoard = () => <Svg><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 3v18M15 3v18" /></Svg>;
const IconBolt = () => <Svg><path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" /></Svg>;
const IconUser = () => <Svg><circle cx="12" cy="8" r="4" /><path d="M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" /></Svg>;
const IconRss = () => <Svg><path d="M4 11a9 9 0 0 1 9 9" /><path d="M4 4a16 16 0 0 1 16 16" /><circle cx="5" cy="19" r="1" /></Svg>;
const IconBook = () => <Svg><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></Svg>;
const IconGit = () => <Svg><circle cx="6" cy="6" r="3" /><circle cx="6" cy="18" r="3" /><path d="M18 9a9 9 0 0 1-9 9" /><circle cx="18" cy="6" r="3" /></Svg>;
const IconCross = () => <Svg><path d="M8 3H5a2 2 0 0 0-2 2v3M21 8V5a2 2 0 0 0-2-2h-3M3 16v3a2 2 0 0 0 2 2h3M16 21h3a2 2 0 0 0 2-2v-3M9 12h6M12 9v6" /></Svg>;

function SourceIcon({ kind }: { kind: SourceKind }) {
  if (kind === 'perfil_social') return <IconUser />;
  if (kind === 'feed') return <IconRss />;
  if (kind === 'periodico') return <IconBook />;
  if (kind === 'cluster') return <IconCross />;
  if (kind === 'documento') return <IconDoc />;
  return <IconGit />;
}
function IconClock() {
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}
function IconDoc() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5M9 13h6M9 17h4" />
    </svg>
  );
}
function RouteIcon({ kind }: { kind: Rota }) {
  if (kind === 'alerta') return <IconBell />;
  if (kind === 'card') return <IconBoard />;
  return <IconBolt />;
}
